import React, { useState, useEffect } from "react"
import { Container, CardBody, Card } from "reactstrap"
import Breadcrumb from "components/Common/Breadcrumb"
import { axiosbaseurl } from "configure"
import Loader from "components/Common/Loader"
import { customStyles } from "helpers/select-styles"
import Select from "react-select"
import moment from "moment"
import * as XLSX from "xlsx"

const BudgetReport = () => {
  const [rawData, setRawData] = useState([]) // Original API data
  const [filteredData, setFilteredData] = useState([]) // Filtered results

  const [client_options, setClientOptions] = useState([])
  const [service_options, setServiceOptions] = useState([])
  const [task_options, setTaskOptions] = useState([])

  const [client, setClient] = useState(null)
  const [service, setService] = useState(null)
  const [task, setTask] = useState(null)

  const [budgetInfo, setBudgetInfo] = useState({
    totalBudgetCost: "£0.00",
    totalTimeSpent: "00:00",
  })

  const [loading, setLoading] = useState(false)

  // Fetch data and prepare options
  const getTaskBudgetReport = async () => {
    try {
      setLoading(true)
      const response = await axiosbaseurl.get(`/reports/task/v2/budget`)
      if (response.data.success) {
        const data = response.data?.data || []
        setRawData(data)
        setFilteredData(data)

        // Clients
        const uniqueClients = [
          ...new Map(
            data
              .filter(item => item.clientId && item.clientName)
              .map(item => [
                item.clientId,
                { value: item.clientId, label: item.clientName },
              ])
          ).values(),
        ]
        setClientOptions(uniqueClients.reverse())

        // Services
        const uniqueServices = [
          ...new Map(
            data
              .filter(item => item.serviceId && item.serviceName)
              .map(item => [
                item.serviceId,
                { value: item.serviceId, label: item.serviceName },
              ])
          ).values(),
        ]
        setServiceOptions(uniqueServices.reverse())

        // Tasks
        const uniqueTasks = [
          ...new Map(
            data
              .filter(item => item.taskId && item.taskName)
              .map(item => [
                item.taskId,
                { value: item.taskId, label: item.taskName },
              ])
          ).values(),
        ]
        setTaskOptions(uniqueTasks.reverse())

        calculateTotals(data)
      }
    } catch (error) {
      console.error("Error fetching budget report:", error)
      setRawData([])
      setFilteredData([])
      setBudgetInfo({
        totalBudgetCost: "£0.00",
        totalTimeSpent: "00:00",
      })
    } finally {
      setLoading(false)
    }
  }

  // Convert HH:mm string to total minutes
  const timeToMinutes = timeStr => {
    if (!timeStr || timeStr === "00:00") return 0
    const [hours, minutes] = timeStr.split(":").map(Number)
    return (hours || 0) * 60 + (minutes || 0)
  }

  // Convert total minutes back to HH:mm
  const minutesToTime = totalMinutes => {
    if (!totalMinutes || totalMinutes <= 0) return "00:00"
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    return `${hours.toString().padStart(2, "0")}:${minutes
      .toString()
      .padStart(2, "0")}`
  }

  // Calculate totals (cost + time)
  const calculateTotals = data => {
    const totalCost = data.reduce(
      (sum, item) => sum + (Number(item.cost) || 0),
      0
    )

    // Sum all timeSpent values
    const totalMinutes = data.reduce((sum, item) => {
      return sum + timeToMinutes(item.timeSpent)
    }, 0)

    setBudgetInfo({
      totalBudgetCost: `£${totalCost.toFixed(2)}`,
      totalTimeSpent: minutesToTime(totalMinutes),
    })
  }

  // Combined filtering
  useEffect(() => {
    let result = [...rawData]

    if (client) {
      result = result.filter(item => item.clientId === client.value)
    }

    if (service) {
      result = result.filter(item => item.serviceId === service.value)
    }

    if (task) {
      result = result.filter(item => item.taskId === task.value)
    }

    setFilteredData(result)
    calculateTotals(result)
  }, [client, service, task, rawData])

  useEffect(() => {
    getTaskBudgetReport()
  }, [])

  const exportToExcel = () => {
    if (filteredData.length === 0) {
      alert("No data to export")
      return
    }

    const exportData = filteredData.map(item => ({
      Client: item.clientName || "--",
      Task: item.taskName || "--",
      Service: item.serviceName || "--",
      Employee: item.employee || "--",
      "Work Type": item.workType || "--",
      "Time Spent": item.timeSpent || "00:00",
      "Budgeted Cost": `£${Number(item.cost || 0).toFixed(2)}`,
      "Proportion of Time Spent": `${Number(
        item.timeSpentProportion || 0
      ).toFixed(1)}%`,
      "Submitted Date": moment(item.createdAt).format("DD/MM/YYYY"),
      "Submitted Time": moment(item.createdAt).format("hh:mm A"),
    }))

    const worksheet = XLSX.utils.json_to_sheet(exportData)

    // Auto-size columns
    const headers = Object.keys(exportData[0] || {})
    worksheet["!cols"] = headers.map(key => ({
      wch: Math.max(
        key.length,
        ...exportData.map(row => String(row[key] || "").length)
      ),
    }))

    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, "Budget Report")

    const filename = `Budget_Report_${moment().format(
      "YYYY-MM-DD_HH-mm-ss"
    )}.xlsx`
    XLSX.writeFile(workbook, filename)
  }

  document.title = "Budget Report | Peacock Accountants Limited"

  return (
    <>
      {loading && <Loader />}

      <div className="page-content mb-5">
        <Container fluid>
          <Breadcrumb title="Dashboard" breadcrumbItem="Budget Report" />

          <Card className="px-0 mx-0">
            <CardBody style={{ minHeight: "400px" }}>
              <div className="row mb-3">
                <div className="col-md-12 mb-4">
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Filter By Client</label>
                      <Select
                        value={client}
                        onChange={setClient}
                        options={client_options}
                        styles={customStyles}
                        placeholder="Select Client"
                        isClearable
                        isSearchable
                      />
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label">Filter By Service</label>
                      <Select
                        value={service}
                        onChange={setService}
                        options={service_options}
                        styles={customStyles}
                        placeholder="Select Service"
                        isClearable
                        isSearchable
                      />
                    </div>

                    <div className="col-md-4 mb-3">
                      <label className="form-label">Filter By Task</label>
                      <Select
                        value={task}
                        onChange={setTask}
                        options={task_options}
                        styles={customStyles}
                        placeholder="Select Task"
                        isClearable
                        isSearchable
                      />
                    </div>
                  </div>
                </div>

                <div className="col-md-12 mb-3">
                  <div className="d-flex justify-content-between mb-4 align-items-center">
                    <h5 className="fw-semibold mb-0">
                      Employee Wise Task Budget Report
                    </h5>
                    <div className="d-flex gap-4">
                      <div className="px-3 py-1 bg-light rounded border border-primary">
                        Total Budget Cost: <b>{budgetInfo.totalBudgetCost}</b>
                      </div>
                      <div className="px-3 py-1 bg-light rounded border border-primary">
                        Total Time Spent: <b>{budgetInfo.totalTimeSpent}</b> hrs
                      </div>
                      <button
                        className="btn btn-success btn-sm px-2"
                        onClick={exportToExcel}
                        disabled={filteredData.length === 0 || loading}
                      >
                        Export to Excel
                      </button>
                    </div>
                  </div>

                  <table className="table table-bordered">
                    <thead className="table-primary">
                      <tr>
                        <th>Employee</th>
                        <th>Work Type</th>
                        <th>Time Spent</th>
                        <th>Budgeted Cost</th>
                        <th>Proportion of Time Spent</th>
                        <th>Submitted At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map(report => (
                        <tr key={report.id}>
                          <td>{report.employee}</td>
                          <td>{report.workType}</td>
                          <td>{report.timeSpent} hrs</td>{" "}
                          {/* ← Now using timeSpent */}
                          <td>£ {report.cost}</td>
                          <td>{report.timeSpentProportion}%</td>
                          <td>
                            {moment(report.createdAt).format(
                              "DD/MM/YYYY • hh:mm A"
                            )}
                          </td>
                        </tr>
                      ))}

                      {filteredData.length === 0 && !loading && (
                        <tr className="text-center">
                          <td colSpan={6}>No Data Available!</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </CardBody>
          </Card>
        </Container>
      </div>
    </>
  )
}

export default BudgetReport
